import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import { Gallery, Item } from 'react-photoswipe-gallery';
import type PhotoSwipe from 'photoswipe';
import 'photoswipe/dist/photoswipe.css';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { SecureVideoPlayer } from '../../components/SecureVideoPlayer';
import { useCopy } from '../../shared/copy/CopyProvider';
import { FollowButton } from '../follow/FollowButton';
import { getReferencePackLicenseLabel } from '../legal/referencePackLicenseLabels';
import { authHeaders, jsonAuthHeaders } from '../../shared/authHeaders';
import { formatMoneyCompact } from '../../shared/money';
import {
  buildReferencePackPoseRows,
  parseRecordingLabel,
  type PurchasedPackRecordingMedia,
  type ReferencePackPoseRow,
} from './purchasedReferencePackMedia';

interface PackCapture {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  width: number | null;
  height: number | null;
  capturedAt: string | null;
  poseName: string | null;
  poseIndex: number | null;
  cameraId: string | null;
  cameraLabel: string | null;
}

interface PackRecording {
  id: string;
  label: string | null;
  poseName: string | null;
  poseIndex: number | null;
  cameraId: string | null;
  cameraLabel: string | null;
  status: string;
  hasVideo: boolean;
  contentType: string | null;
  durationSeconds: number | null;
  startedAt: string | null;
  endedAt: string | null;
}

interface PurchasedPackDetail {
  product: {
    id: string;
    title: string;
    description: string | null;
    license: string;
    coverImageUrl: string | null;
    accessExpiresOn: string | null;
    room: {
      code: string;
      name: string;
      scheduledAt: string | null;
    };
    purchase: {
      completedAt: string | null;
      accessExpiresOn: string | null;
    };
    sellerProfile: {
      id: string;
      displayName: string;
      username: string | null;
      photoUrl: string | null;
    } | null;
  };
  captures: PackCapture[];
  recordings: PackRecording[];
}

interface PublicPackDetail {
  product: {
    id: string;
    title: string;
    description: string | null;
    accessType: 'sellable' | 'free' | 'private';
    priceCents: number | null;
    currency: string;
    license: string;
    coverImageUrl: string | null;
    accessExpiresOn: string | null;
    accessDurationCount: number | null;
    accessDurationUnit: 'days' | 'weeks' | 'months' | 'years' | null;
    captureIds: string[];
    recordingIds: string[];
    requiresBuyerApproval: boolean;
    purchased: boolean;
    purchaseAccessExpiresOn: string | null;
    purchaseUrl: string;
    room: {
      code: string;
      name: string;
      scheduledAt: string | null;
    };
    sellerProfile: {
      id: string;
      displayName: string;
      username: string | null;
      photoUrl: string | null;
    } | null;
  };
  captures: PackCapture[];
}

interface PackAlbumPhoto {
  src: string;
  fullSrc: string;
  width: number;
  height: number;
  resolutionWidth: number | null;
  resolutionHeight: number | null;
  key: string;
  poseName: string | null;
  cameraLabel: string | null;
  alt: string;
}

type PackPoseMediaTile =
  | {
    type: 'recording';
    key: string;
    timestampMs: number | null;
    recording: PurchasedPackRecordingMedia;
  }
  | {
    type: 'photo';
    key: string;
    timestampMs: number | null;
    photo: PackAlbumPhoto;
  };

const MAX_POSE_MEDIA_COLUMNS = 5;

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(cents: number | null, currency: string) {
  if (cents === null) return null;
  return formatMoneyCompact(cents, currency);
}

function formatAccessDuration(count: number | null, unit: PublicPackDetail['product']['accessDurationUnit']) {
  if (!count || !unit) return null;
  const singularUnit = count === 1 ? unit.replace(/s$/, '') : unit;
  return `${count} ${singularUnit}`;
}

function getLicenseLabel(license: string) {
  return getReferencePackLicenseLabel(license) ?? license.replaceAll('_', ' ');
}

function formatDuration(seconds: number | null) {
  if (seconds === null) return null;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

function formatMediaTime(timestampMs: number | null) {
  if (timestampMs === null) return null;
  return new Date(timestampMs).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getTimestampMs(dateStr: string | null) {
  if (!dateStr) return null;
  const timestamp = new Date(dateStr).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function getProfilePath(profile: { id: string; username: string | null }) {
  return profile.username ? `/u/${profile.username}` : `/profile/${profile.id}`;
}

function getCameraDisplayLabel(cameraLabel: string | null | undefined) {
  return cameraLabel?.trim() || 'Unlabeled camera';
}

function getRecordingCameraLabel(recording: PurchasedPackRecordingMedia) {
  return getCameraDisplayLabel(recording.cameraLabel ?? parseRecordingLabel(recording.label).cameraLabel);
}

function formatResolution(width: number | null | undefined, height: number | null | undefined) {
  if (!width || !height) return 'Unknown resolution';
  return `${width} x ${height}`;
}

function preventLightboxImageSave(photoswipe: PhotoSwipe) {
  const root = photoswipe.element;
  if (!root) return;

  const preventImageSave = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.pswp__img')) return;

    event.preventDefault();
  };

  root.style.setProperty('-webkit-touch-callout', 'none');
  root.style.userSelect = 'none';
  root.addEventListener('contextmenu', preventImageSave);
  root.addEventListener('dragstart', preventImageSave);

  photoswipe.on('destroy', () => {
    root.removeEventListener('contextmenu', preventImageSave);
    root.removeEventListener('dragstart', preventImageSave);
  });
}

export function PurchasedReferencePackPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const checkoutPayment = searchParams.get('payment');
  const checkoutSessionId = searchParams.get('session_id');
  const { t } = useCopy();
  const [data, setData] = useState<PurchasedPackDetail | null>(null);
  const [publicData, setPublicData] = useState<PublicPackDetail | null>(null);
  const [imageDimensions, setImageDimensions] = useState<Record<string, { width: number; height: number }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Withdrawn / revoked / unpublished-after-purchase: the API answers 410 and
  // the page shows the approved copy instead of a not-found error.
  const [unavailable, setUnavailable] = useState(false);
  const [purchasingPublicPack, setPurchasingPublicPack] = useState(false);

  useEffect(() => {
    if (!productId || !isLoaded) return;

    async function loadPack() {
      const loadPublicPack = async (token: string | null) => {
        const publicResponse = await fetch(`/api/products/reference-packs/public/${productId}`, {
          headers: authHeaders(token),
        });
        const publicResponseData = await publicResponse.json().catch(() => ({}));

        if (publicResponse.status === 410) {
          setData(null);
          setPublicData(null);
          setUnavailable(true);
          return;
        }

        if (!publicResponse.ok) {
          throw new Error(publicResponse.status === 404 ? 'Reference pack not found' : 'Failed to load reference pack');
        }

        setData(null);
        setPublicData(publicResponseData);
      };

      try {
        setLoading(true);
        setError(null);
        setUnavailable(false);
        const token = isSignedIn ? await getToken() : null;
        if (isSignedIn && checkoutPayment === 'reference_pack_success' && checkoutSessionId) {
          const confirmResponse = await fetch('/api/products/reference-packs/checkout/confirm-session', {
            method: 'POST',
            headers: jsonAuthHeaders(token),
            body: JSON.stringify({ sessionId: checkoutSessionId }),
          });
          const confirmData = await confirmResponse.json().catch(() => ({}));

          if (!confirmResponse.ok) {
            throw new Error(confirmData.error || 'Failed to confirm reference pack purchase');
          }

          navigate(`/dashboard/packs/${confirmData.productId || productId}`, { replace: true });
        }

        if (!isSignedIn) {
          await loadPublicPack(null);
          return;
        }

        const response = await fetch(`/api/products/reference-packs/purchased/${productId}`, {
          headers: authHeaders(token),
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (response.status === 410) {
            setData(null);
            setPublicData(null);
            setUnavailable(true);
            return;
          }

          if (response.status === 401 || response.status === 403) {
            await loadPublicPack(token);
            return;
          }

          throw new Error(response.status === 404 ? 'Reference pack not found' : 'Failed to load reference pack');
        }

        setPublicData(null);
        setData(responseData);
      } catch (err) {
        console.error('Error loading reference pack:', err);
        setError(err instanceof Error ? err.message : 'Failed to load reference pack');
      } finally {
        setLoading(false);
      }
    }

    void loadPack();
  }, [productId, getToken, isLoaded, isSignedIn, checkoutPayment, checkoutSessionId, navigate]);

  const handlePublicPackPurchase = async () => {
    if (!publicData) return;

    if (!isSignedIn) {
      window.location.href = `/sign-in?redirect_url=${encodeURIComponent(`/dashboard/packs/${publicData.product.id}`)}`;
      return;
    }

    if (publicData.product.requiresBuyerApproval) {
      navigate(publicData.product.purchaseUrl);
      return;
    }

    setPurchasingPublicPack(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/${publicData.product.id}/purchase`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          successUrl: `${window.location.origin}/dashboard/packs/${publicData.product.id}?payment=reference_pack_success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/dashboard/packs/${publicData.product.id}`,
        }),
      });
      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to start checkout');
      }

      window.location.href = responseData.url || `/dashboard/packs/${publicData.product.id}`;
    } catch (err) {
      console.error('Error buying reference pack:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setPurchasingPublicPack(false);
    }
  };

  useEffect(() => {
    if (!data) return;

    data.captures.forEach((capture) => {
      if (capture.width && capture.height) {
        setImageDimensions((prev) => prev[capture.id] ? prev : {
          ...prev,
          [capture.id]: { width: capture.width ?? 4, height: capture.height ?? 5 },
        });
        return;
      }

      if (!imageDimensions[capture.id]) {
        const img = new Image();
        img.onload = () => {
          setImageDimensions((prev) => ({
            ...prev,
            [capture.id]: { width: img.naturalWidth || 4, height: img.naturalHeight || 5 },
          }));
        };
        img.src = capture.imageUrl;
      }
    });
  }, [data, imageDimensions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg pt-[88px]">
        <NavBar variant="light" />
        <main className="page-container py-16">
          <p className="text-text-dim">Loading reference pack...</p>
        </main>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="min-h-screen bg-bg pt-[88px]">
        <NavBar variant="light" />
        <main className="page-container py-16 text-center">
          <h1 className="page-heading">Reference pack</h1>
          <p className="mt-3 text-text-dim">{t('errors.referencePackNoLongerAvailable')}</p>
          <button type="button" onClick={() => navigate('/dashboard')} className="btn btn-accent mt-6">
            Back to dashboard
          </button>
        </main>
      </div>
    );
  }

  if (publicData) {
    return (
      <PublicReferencePackPage
        data={publicData}
        isSignedIn={Boolean(isSignedIn)}
        purchasing={purchasingPublicPack}
        onPurchase={handlePublicPackPurchase}
      />
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-bg pt-[88px]">
        <NavBar variant="light" />
        <main className="page-container py-16 text-center">
          <h1 className="page-heading">Reference pack unavailable</h1>
          <p className="mt-3 text-text-dim">{error}</p>
          <button type="button" onClick={() => navigate('/dashboard')} className="btn btn-accent mt-6">
            Back to dashboard
          </button>
        </main>
      </div>
    );
  }

  const albumPhotos = data.captures.map((capture) => {
    const measuredDims = imageDimensions[capture.id];
    const resolutionWidth = measuredDims?.width ?? capture.width;
    const resolutionHeight = measuredDims?.height ?? capture.height;
    return {
      src: capture.imageUrl,
      fullSrc: capture.imageUrl,
      width: resolutionWidth || 4,
      height: resolutionHeight || 5,
      resolutionWidth,
      resolutionHeight,
      key: capture.id,
      poseName: capture.poseName,
      cameraLabel: capture.cameraLabel,
      alt: capture.poseName || data.product.title,
    };
  });
  const albumPhotoById = new Map(albumPhotos.map((photo) => [photo.key, photo]));
  const mediaLayout = buildReferencePackPoseRows(data.captures, data.recordings);
  const purchasedAt = formatDate(data.product.purchase.completedAt);
  const accessExpiresOn = formatDate(data.product.purchase.accessExpiresOn || data.product.accessExpiresOn);

  return (
    <div className="min-h-screen flex flex-col bg-bg pt-[88px]">
      <NavBar variant="light" />

      <main className="flex-1">
        <section className="pb-10">
          <div className="page-container">
            <Link to="/dashboard" className="text-sm text-text-dim border-b border-line pb-px hover:text-text hover:border-text">
              Back to dashboard
            </Link>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-10 lg:gap-16 items-end">
              <div>
                {data.product.sellerProfile && (
                  <div className="mb-10 flex items-center gap-5">
                    <Link
                      to={getProfilePath(data.product.sellerProfile)}
                      className="shrink-0 text-text hover:text-text-dim transition-colors"
                    >
                      {data.product.sellerProfile.photoUrl ? (
                        <img
                          src={data.product.sellerProfile.photoUrl}
                          alt={data.product.sellerProfile.displayName}
                          className="h-24 w-24 rounded-full object-cover border border-line-soft"
                        />
                      ) : (
                        <span className="flex h-24 w-24 items-center justify-center rounded-full border border-line-soft bg-bg-secondary text-2xl font-semibold text-text-dim">
                          {data.product.sellerProfile.displayName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </Link>
                    <div className="flex min-w-0 flex-col items-start gap-3">
                      <Link
                        to={getProfilePath(data.product.sellerProfile)}
                        className="text-text hover:text-text-dim transition-colors"
                      >
                      <span>
                        <span className="block text-[11px] uppercase text-text-dim">Model</span>
                        <span className="block text-[22px] leading-tight border-b border-line pb-px">
                          {data.product.sellerProfile.displayName}
                        </span>
                      </span>
                      </Link>
                      <FollowButton
                        profileId={data.product.sellerProfile.id}
                        followLabel="Follow"
                        followingLabel="Following"
                      />
                    </div>
                  </div>
                )}
                <div className="page-eyebrow before:hidden">
                  Reference pack
                </div>
                <h1 className="page-heading">
                  {data.product.title}
                </h1>
                {data.product.description && (
                  <p className="mt-6 max-w-[680px] text-[18px] leading-[1.6] text-text-dim">
                    {data.product.description}
                  </p>
                )}
              </div>

              <div className="border-t border-l border-line bg-white/40">
                <div className="grid grid-cols-2">
                  <PackStat
                    label="Photos"
                    value={data.captures.length}
                  />
                  <PackStat
                    label="Videos"
                    value={data.recordings.length}
                  />
                  <PackStat label="Session" value={data.product.room.name} wide />
                  <PackStat
                    label="License"
                    value={(
                      <Link to={`/reference-pack-licenses#${data.product.license}`} className="border-b border-line pb-px hover:border-text">
                        {getLicenseLabel(data.product.license)}
                      </Link>
                    )}
                    wide
                  />
                  {purchasedAt && <PackStat label="Purchased" value={purchasedAt} />}
                  {accessExpiresOn && <PackStat label="Access until" value={accessExpiresOn} />}
                </div>
              </div>
            </div>
          </div>
        </section>

        {(data.recordings.length > 0 || albumPhotos.length > 0) && (
          <section id="reference-media" className="scroll-mt-28 py-10 pb-20 border-t border-line">
            <div className="page-container">
              <Gallery withCaption={false} onOpen={preventLightboxImageSave}>
                <div className="space-y-8">
                  {mediaLayout.rows.map((row) => {
                    const rowTime = formatMediaTime(row.timestampMs);
                    const mediaItems = getPoseMediaTiles(row, albumPhotoById);
                    const mediaRows = getPoseMediaRows(mediaItems);

                    return (
                      <article key={row.key} className="overflow-hidden rounded-lg border border-line bg-white">
                        <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line bg-bg px-4 py-4 sm:px-5">
                          <h3 className="text-[24px] leading-tight text-text">{row.label}</h3>
                          <div className="flex flex-wrap items-center justify-end gap-3 text-sm text-text-dim">
                            {rowTime && <span>{rowTime}</span>}
                            <span>
                              {mediaItems.length} item{mediaItems.length === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>

                        <div>
                          {mediaItems.length === 0 ? (
                            <p className="rounded border border-dashed border-line-soft bg-bg-secondary px-3 py-8 text-center text-sm text-text-dim">
                              No media
                            </p>
                          ) : (
                            <div>
                              {mediaRows.map((mediaRow) => (
                                <div
                                  key={mediaRow.map((item) => item.key).join(':')}
                                  className="grid"
                                  style={getPoseMediaGridStyle(mediaRow.length)}
                                >
                                  {mediaRow.map((item) => (
                                    item.type === 'recording' ? (
                                      <ReferencePackVideoTile key={item.key} recording={item.recording} />
                                    ) : (
                                      <ReferencePackPhotoTile key={item.key} photo={item.photo} />
                                    )
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </Gallery>
            </div>
          </section>
        )}
      </main>

      <SharedFooter />
    </div>
  );
}

function getPoseMediaTiles(
  row: ReferencePackPoseRow,
  albumPhotoById: Map<string, PackAlbumPhoto>
): PackPoseMediaTile[] {
  const items: PackPoseMediaTile[] = [];

  row.cells.forEach((cell) => {
    cell.recordings.forEach((recording) => {
      items.push({
        type: 'recording',
        key: `recording:${recording.id}`,
        timestampMs: getTimestampMs(recording.startedAt) ?? getTimestampMs(recording.endedAt),
        recording,
      });
    });

    cell.captures.forEach((capture) => {
      const photo = albumPhotoById.get(capture.id);
      if (!photo) return;

      items.push({
        type: 'photo',
        key: `photo:${capture.id}`,
        timestampMs: getTimestampMs(capture.capturedAt),
        photo,
      });
    });
  });

  return items.sort((a, b) => {
    if (a.timestampMs !== null && b.timestampMs !== null && a.timestampMs !== b.timestampMs) {
      return a.timestampMs - b.timestampMs;
    }
    if (a.timestampMs !== null && b.timestampMs === null) return -1;
    if (a.timestampMs === null && b.timestampMs !== null) return 1;
    if (a.type !== b.type) return a.type === 'recording' ? -1 : 1;
    return a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: 'base' });
  });
}

function getPoseMediaRows(items: PackPoseMediaTile[]) {
  const rows: PackPoseMediaTile[][] = [];

  for (let index = 0; index < items.length; index += MAX_POSE_MEDIA_COLUMNS) {
    rows.push(items.slice(index, index + MAX_POSE_MEDIA_COLUMNS));
  }

  return rows;
}

function getPoseMediaGridStyle(itemCount: number): CSSProperties {
  const columnCount = Math.max(1, Math.min(itemCount, MAX_POSE_MEDIA_COLUMNS));
  return {
    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
  };
}

function hasThumbnailUrl(capture: PackCapture): capture is PackCapture & { thumbnailUrl: string } {
  return Boolean(capture.thumbnailUrl);
}

function PublicReferencePackPage({
  data,
  isSignedIn,
  purchasing,
  onPurchase,
}: {
  data: PublicPackDetail;
  isSignedIn: boolean;
  purchasing: boolean;
  onPurchase: () => void;
}) {
  const previewCaptures = data.captures.filter(hasThumbnailUrl);
  const coverImageUrl = data.product.coverImageUrl || previewCaptures[0]?.thumbnailUrl || null;
  const priceLabel = data.product.accessType === 'free'
    ? 'Free'
    : formatMoney(data.product.priceCents, data.product.currency);
  const accessDurationLabel = formatAccessDuration(data.product.accessDurationCount, data.product.accessDurationUnit);
  const scheduledAt = data.product.room.scheduledAt
    ? new Date(data.product.room.scheduledAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  const signedOutActionLabel = data.product.requiresBuyerApproval
    ? 'Sign in to request access'
    : data.product.accessType === 'free' || data.product.priceCents === 0
      ? 'Sign in to get access'
      : 'Sign in to purchase';
  const signedInActionLabel = data.product.requiresBuyerApproval
    ? 'Request access'
    : data.product.accessType === 'free' || data.product.priceCents === 0
      ? 'Get free access'
      : priceLabel
        ? `Buy for ${priceLabel}`
        : 'Buy reference pack';
  const actionLabel = isSignedIn ? signedInActionLabel : signedOutActionLabel;
  const previewImageClassName = isSignedIn ? '' : 'blur-md scale-[1.04]';

  return (
    <div className="min-h-screen flex flex-col bg-bg pt-[88px]">
      <NavBar variant="light" />

      <main className="flex-1">
        <section className="pb-12">
          <div className="page-container">
            <Link
              to={`/s/${data.product.room.code}/summary`}
              className="text-sm text-text-dim border-b border-line pb-px hover:text-text hover:border-text"
            >
              Back to event summary
            </Link>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-10 lg:gap-16 items-end">
              <div>
                {data.product.sellerProfile && (
                  <div className="mb-10 flex items-center gap-5">
                    <Link
                      to={getProfilePath(data.product.sellerProfile)}
                      className="shrink-0 text-text hover:text-text-dim transition-colors"
                    >
                      {data.product.sellerProfile.photoUrl ? (
                        <img
                          src={data.product.sellerProfile.photoUrl}
                          alt={data.product.sellerProfile.displayName}
                          className="h-24 w-24 rounded-full object-cover border border-line-soft"
                        />
                      ) : (
                        <span className="flex h-24 w-24 items-center justify-center rounded-full border border-line-soft bg-bg-secondary text-2xl font-semibold text-text-dim">
                          {data.product.sellerProfile.displayName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </Link>
                    <div className="flex min-w-0 flex-col items-start gap-3">
                      <Link
                        to={getProfilePath(data.product.sellerProfile)}
                        className="text-text hover:text-text-dim transition-colors"
                      >
                        <span>
                          <span className="block text-[11px] uppercase text-text-dim">Model</span>
                          <span className="block text-[22px] leading-tight border-b border-line pb-px">
                            {data.product.sellerProfile.displayName}
                          </span>
                        </span>
                      </Link>
                      <FollowButton
                        profileId={data.product.sellerProfile.id}
                        followLabel="Follow"
                        followingLabel="Following"
                      />
                    </div>
                  </div>
                )}

                <div className="page-eyebrow before:hidden">Reference pack</div>
                <h1 className="page-heading">{data.product.title}</h1>
                {data.product.description && (
                  <p className="mt-6 max-w-[680px] text-[18px] leading-[1.6] text-text-dim">
                    {data.product.description}
                  </p>
                )}
              </div>

              <div className="border-t border-l border-line bg-white/40">
                <div className="grid grid-cols-2">
                  <PackStat label="Photos" value={data.product.captureIds.length} />
                  <PackStat label="Videos" value={data.product.recordingIds.length} />
                  <PackStat label="Price" value={priceLabel ?? 'Unavailable'} />
                  {scheduledAt && <PackStat label="Session date" value={scheduledAt} />}
                  <PackStat
                    label="License"
                    value={(
                      <Link to={`/reference-pack-licenses#${data.product.license}`} className="border-b border-line pb-px hover:border-text">
                        {getLicenseLabel(data.product.license)}
                      </Link>
                    )}
                    wide
                  />
                  {data.product.accessExpiresOn ? (
                    <PackStat label="Access until" value={data.product.accessExpiresOn} />
                  ) : accessDurationLabel ? (
                    <PackStat label="Access length" value={accessDurationLabel} />
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 lg:gap-12">
              <div className="overflow-hidden rounded-lg border border-line bg-white">
                {coverImageUrl ? (
                  <img
                    src={coverImageUrl}
                    alt={data.product.title}
                    className={`aspect-[4/3] w-full object-cover ${previewImageClassName}`}
                    draggable={false}
                    onContextMenu={(event) => event.preventDefault()}
                  />
                ) : (
                  <div className="aspect-[4/3] w-full bg-bg-secondary" />
                )}
                {previewCaptures.length > 1 && (
                  <div className="grid grid-cols-4 border-t border-line">
                    {previewCaptures.slice(0, 8).map((capture) => (
                      <img
                        key={capture.id}
                        src={capture.thumbnailUrl}
                        alt={capture.poseName || data.product.title}
                        className={`aspect-square w-full object-cover ${previewImageClassName}`}
                        draggable={false}
                        onContextMenu={(event) => event.preventDefault()}
                      />
                    ))}
                  </div>
                )}
              </div>

              <aside className="rounded-lg border border-line bg-white p-5 self-start">
                <h2 className="font-display text-2xl leading-tight">Full-resolution access</h2>
                <p className="mt-3 text-sm leading-6 text-text-dim">
                  {isSignedIn
                    ? 'Purchase or request access to the full reference pack.'
                    : 'Sign in to purchase or request access to the full reference pack.'}
                </p>
                {data.product.requiresBuyerApproval && (
                  <p className="mt-3 text-sm leading-6 text-text-dim">
                    The model reviews access requests before checkout opens.
                  </p>
                )}
                <button
                  type="button"
                  onClick={onPurchase}
                  disabled={purchasing}
                  className="btn btn-accent mt-5 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {purchasing ? 'Opening...' : actionLabel}
                </button>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <SharedFooter />
    </div>
  );
}

function ReferencePackVideoTile({ recording }: { recording: PurchasedPackRecordingMedia }) {
  const recordingMeta = [
    getRecordingCameraLabel(recording),
    formatDuration(recording.durationSeconds),
  ].filter(Boolean).join(' · ');

  return (
    <div className="relative aspect-square overflow-hidden bg-black">
      <SecureVideoPlayer
        recordingId={recording.id}
        hasVideo={recording.hasVideo}
        contentType={recording.contentType}
        status={recording.status}
        durationSeconds={recording.durationSeconds}
        startedAt={recording.startedAt}
        allowDownload={false}
        showDownloadButton={false}
        showDetailsFooter={false}
        showPreview
        playButtonPlacement="bottom-left"
        aspectClassName="aspect-square"
        dimPreview={false}
        openFullscreenOnMobilePlay
        className="h-full w-full bg-black"
      />
      <div className="pointer-events-none absolute left-2 right-2 top-2 z-10">
        <div className="inline-block max-w-full rounded bg-black/70 px-2.5 py-1.5 text-white shadow-sm">
          {recordingMeta && (
            <p className="truncate text-[11px] font-medium leading-tight text-white/85">{recordingMeta}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ReferencePackPhotoTile({ photo }: { photo: PackAlbumPhoto }) {
  const cameraLabel = getCameraDisplayLabel(photo.cameraLabel);
  const resolutionLabel = formatResolution(photo.resolutionWidth, photo.resolutionHeight);
  const photoLabel = `${cameraLabel} · ${resolutionLabel}`;

  return (
    <Item<HTMLButtonElement>
      original={photo.fullSrc}
      thumbnail={photo.src}
      width={photo.width}
      height={photo.height}
    >
      {({ ref, open }) => (
        <button
          type="button"
          ref={ref}
          onClick={open}
          onContextMenu={(event) => event.preventDefault()}
          title={photoLabel}
          className="group relative block aspect-square overflow-hidden bg-bg-secondary p-0 text-left cursor-zoom-in"
        >
          <img
            src={photo.src}
            alt={photo.alt}
            draggable={false}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            onContextMenu={(event) => event.preventDefault()}
          />
          <div className="pointer-events-none absolute left-2 right-2 top-2 z-10">
            <div className="inline-block max-w-full rounded bg-black/70 px-2.5 py-1.5 text-white shadow-sm">
              <p className="truncate text-[11px] font-medium leading-tight text-white/85">
                {photoLabel}
              </p>
            </div>
          </div>
        </button>
      )}
    </Item>
  );
}

function PackStat({ label, value, wide = false }: { label: string; value: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`p-5 border-r border-b border-line ${wide ? 'col-span-2' : ''}`}>
      <p className="text-[10.5px] uppercase text-text-dim mb-2">{label}</p>
      <p className="text-[15px] text-text leading-snug">{value}</p>
    </div>
  );
}
